import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabaseClient } from '../../services/supabase';
import { formatDate, formatDateForDisplay, formatTime } from '../../utils/dateUtils';
import DatePickerCalendar from './DatePickerCalendar';

// メール送信API呼び出し
const sendEmail = async (type, reservation) => {
  const response = await fetch('/api/send-email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ type, reservation }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'メール送信に失敗しました');
  }

  return response.json();
};

function ReservationSection({ storeConfig, holidays }) {
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    adultCount: 1,
    childCount: 0,
    seatType: '',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [availableTimes, setAvailableTimes] = useState([]);
  const [availableSeats, setAvailableSeats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [reservationComplete, setReservationComplete] = useState(null); // 完了情報を保持
  const completeSectionRef = useRef(null);

  // 非同期処理の完了順が前後しても古い結果で上書きしないためのリクエストID
  const timesRequestId = useRef(0);
  const seatsRequestId = useRef(0);

  const loadAvailableTimes = useCallback(async () => {
    if (!formData.date) return;
    const requestId = ++timesRequestId.current;
    setLoading(true);
    try {
      const effectiveCount = formData.adultCount + Math.ceil(formData.childCount / 2);
      const times = await generateAvailableTimes(formData.date, effectiveCount, formData.childCount > 0);
      if (requestId !== timesRequestId.current) return; // 古いリクエストの結果は破棄
      setAvailableTimes(times);
      // 選択中の時間が引き続き空いていれば維持し、空いていなければリセット
      setFormData(prev => {
        if (prev.time && times.includes(prev.time)) return prev;
        return { ...prev, time: '', seatType: '' };
      });
    } catch (error) {
      console.error('時間取得エラー:', error);
    } finally {
      if (requestId === timesRequestId.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date, formData.adultCount, formData.childCount, storeConfig]);

  const loadAvailableSeats = useCallback(async () => {
    if (!formData.date || !formData.time) {
      setAvailableSeats([]);
      return;
    }
    const requestId = ++seatsRequestId.current;
    try {
      const effectiveCount = formData.adultCount + Math.ceil(formData.childCount / 2);
      const hasChild = formData.childCount > 0;
      const seats = await getAvailableSeats(formData.date, formData.time, effectiveCount, hasChild);
      if (requestId !== seatsRequestId.current) return; // 古いリクエストの結果は破棄
      setAvailableSeats(seats);
      // 選択中の席種が引き続き選択可能であれば維持し、選べなければリセット
      setFormData(prev => {
        if (prev.seatType && seats.some(s => s.value === prev.seatType)) return prev;
        if (!prev.seatType) return prev;
        return { ...prev, seatType: '' };
      });
    } catch (error) {
      console.error('席取得エラー:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date, formData.time, formData.adultCount, formData.childCount, storeConfig]);

  // 日付が変更されたら時間を取得
  useEffect(() => {
    loadAvailableTimes();
  }, [loadAvailableTimes]);

  // 時間が変更されたら席を取得
  useEffect(() => {
    loadAvailableSeats();
  }, [loadAvailableSeats]);

  const generateAvailableTimes = async (date, totalCount, hasChild) => {
    const times = [];
    const startHour = storeConfig.businessHours.start;
    const endHour = storeConfig.businessHours.end;
    const lastOrder = storeConfig.lastOrder;
    const reservationDurationHours = (storeConfig.reservationDuration || 90) / 60;
    const closingBufferHours = 0.5;
    const maxGuestsPerSlot = storeConfig.maxGuestsPerSlot || 6;

    // 予約データを取得（空き状況の計算に必要な項目のみ。個人情報は含まない）
    const { data: reservations } = await supabaseClient
      .rpc('get_reservation_slots', { p_date: date });

    const now = new Date();
    const selectedDate = new Date(date);
    const isToday = now.toDateString() === selectedDate.toDateString();
    const currentHour = now.getHours() + now.getMinutes() / 60;

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeHour = hour + minute / 60;
        if (timeHour > lastOrder) break;  // ラストオーダー時刻は含める
        if (timeHour + reservationDurationHours > endHour + closingBufferHours) continue;
        if (isToday && timeHour <= currentHour) continue;

        const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        
        // 同時刻の予約人数チェック
        const guestsAtTime = getGuestsCountAtTime(reservations || [], timeString);
        if (guestsAtTime + totalCount > maxGuestsPerSlot) {
          continue; // 予約人数上限を超える場合はスキップ
        }
        
        // 空席チェック
        const isAvailable = await checkAvailability(reservations || [], timeString, totalCount, hasChild);
        if (isAvailable) {
          times.push(timeString);
        }
      }
    }

    return times;
  };

  // 指定時刻の予約人数を取得（予約開始時間から30分間のみをチェック）
  const getGuestsCountAtTime = (reservations, time) => {
    const limitCheckDuration = 30; // 予約人数制限は開始時間から30分間のみ
    const timeMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    
    let totalGuests = 0;

    reservations.forEach(res => {
      const resTime = res.reservation_time;
      const resMinutes = parseInt(resTime.split(':')[0]) * 60 + parseInt(resTime.split(':')[1]);
      
      // 予約開始時間から30分間以内かチェック
      // 同じ時間または、指定時刻が既存予約の開始時間から30分以内、または既存予約の開始時間が指定時刻から30分以内
      const timeDiff = Math.abs(timeMinutes - resMinutes);
      if (timeDiff < limitCheckDuration) {
        totalGuests += (res.adult_count || 0) + (res.child_count || 0);
      }
    });

    return totalGuests;
  };

  // 必要なテーブル数を計算（大人5名以上で2卓必要）
  const getRequiredTableCount = (adultCount) => {
    return adultCount >= 5 ? 2 : 1;
  };

  const checkAvailability = async (reservations, time, totalCount, hasChild) => {
    const reservationDuration = storeConfig.reservationDuration || 90;
    const timeMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    
    let usedCounterSeats = 0;
    let usedTableSeats = 0;

    reservations.forEach(res => {
      const resTime = res.reservation_time;
      const resMinutes = parseInt(resTime.split(':')[0]) * 60 + parseInt(resTime.split(':')[1]);
      
      if (Math.abs(timeMinutes - resMinutes) < reservationDuration) {
        if (res.seat_type === 'counter') {
          usedCounterSeats += res.adult_count;
        } else {
          // テーブル予約の卓数を取得（table_countがない場合は1卓とみなす）
          usedTableSeats += res.table_count || 1;
        }
      }
    });

    const counterAvailable = storeConfig.seats.counter.count - usedCounterSeats;
    const tableAvailable = storeConfig.seats.table.count - usedTableSeats;
    const requiredTables = getRequiredTableCount(formData.adultCount);

    // 子供がいる場合はテーブル席のみ
    if (hasChild) {
      return tableAvailable >= requiredTables;
    }

    // カウンター席に収まる場合
    if (totalCount <= storeConfig.seats.counter.maxCapacity) {
      return counterAvailable >= totalCount || tableAvailable >= requiredTables;
    }

    // テーブル席のみ
    return tableAvailable >= requiredTables;
  };

  const getAvailableSeats = async (date, time, totalCount, hasChild) => {
    const seats = [];
    
    // 予約データを取得（空き状況の計算に必要な項目のみ。個人情報は含まない）
    const { data: reservations } = await supabaseClient
      .rpc('get_reservation_slots', { p_date: date });

    const reservationDuration = storeConfig.reservationDuration || 90;
    const timeMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    
    let usedCounterSeats = 0;
    let usedTableSeats = 0;

    (reservations || []).forEach(res => {
      const resTime = res.reservation_time;
      const resMinutes = parseInt(resTime.split(':')[0]) * 60 + parseInt(resTime.split(':')[1]);
      
      if (Math.abs(timeMinutes - resMinutes) < reservationDuration) {
        if (res.seat_type === 'counter') {
          usedCounterSeats += res.adult_count;
        } else {
          // テーブル予約の卓数を取得（table_countがない場合は1卓とみなす）
          usedTableSeats += res.table_count || 1;
        }
      }
    });

    const counterAvailable = storeConfig.seats.counter.count - usedCounterSeats;
    const tableAvailable = storeConfig.seats.table.count - usedTableSeats;
    const requiredTables = getRequiredTableCount(formData.adultCount);

    // カウンター席（子供がいない場合、かつ4名以下のみ）
    if (!hasChild && totalCount <= storeConfig.seats.counter.maxCapacity && counterAvailable >= totalCount && formData.adultCount <= 4) {
      seats.push({ value: 'counter', label: 'カウンター席' });
    }

    // テーブル席（必要な卓数が空いている場合）
    // 大人5〜8名の場合は2卓使用
    if (tableAvailable >= requiredTables) {
      if (requiredTables === 2) {
        seats.push({ value: 'table', label: 'テーブル席（2卓使用）' });
      } else {
        seats.push({ value: 'table', label: 'テーブル席' });
      }
    }

    return seats;
  };

  const getSeatLabel = () => {
    const seat = availableSeats.find(s => s.value === formData.seatType);
    if (seat) return seat.label;
    return formData.seatType === 'counter' ? 'カウンター席' : 'テーブル席';
  };

  const validateForm = () => {
    if (!formData.date || !formData.time || !formData.seatType || !formData.name || !formData.phone || !formData.email) {
      alert('必須項目を入力してください。');
      return false;
    }

    if (formData.childCount > 0 && formData.seatType === 'counter') {
      alert('お子様連れの場合、カウンター席はご利用いただけません。');
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    if (submitting) return;
    setShowConfirmModal(false);
  };

  const handleConfirmReservation = async () => {
    if (!validateForm()) {
      setShowConfirmModal(false);
      return;
    }

    setSubmitting(true);
    try {
      // 予約を登録（予約番号の生成・重複チェック・入力検証はサーバー側で行う）
      const { data: reservationCode, error } = await supabaseClient.rpc('create_reservation', {
        p_date: formData.date,
        p_time: formData.time,
        p_adult: parseInt(formData.adultCount),
        p_child: parseInt(formData.childCount),
        p_seat_type: formData.seatType,
        p_name: formData.name,
        p_phone: formData.phone,
        p_email: formData.email,
        p_notes: formData.notes || null
      });

      if (error) throw error;
      if (!reservationCode) {
        throw new Error('予約番号の取得に失敗しました。');
      }

      // メール送信データを準備
      const reservationData = {
        code: reservationCode,
        date: formData.date,
        dateDisplay: formatDateForDisplay(formData.date),
        time: formatTime(formData.time),
        name: formData.name,
        adultCount: formData.adultCount,
        childCount: formData.childCount,
        seatType: formData.seatType,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes
      };

      // 予約受付メールを送信（お客様向け）
      try {
        await sendEmail('reservation_received', reservationData);
      } catch (emailError) {
        console.error('メール送信エラー:', emailError);
        // メール送信に失敗しても予約自体は成功しているので続行
      }

      // 管理者への通知メールを送信
      try {
        await sendEmail('admin_notification', reservationData);
      } catch (emailError) {
        console.error('管理者通知メール送信エラー:', emailError);
      }

      // 予約完了情報を設定
      setReservationComplete({
        code: reservationCode,
        date: formData.date,
        time: formData.time,
        name: formData.name,
        adultCount: formData.adultCount,
        childCount: formData.childCount,
        seatType: formData.seatType,
        email: formData.email
      });

      setFormData({
        date: '',
        time: '',
        adultCount: 1,
        childCount: 0,
        seatType: '',
        name: '',
        phone: '',
        email: '',
        notes: ''
      });
      setAvailableTimes([]);
      setAvailableSeats([]);
      setShowConfirmModal(false);
    } catch (error) {
      console.error('予約エラー:', error);
      alert('予約の登録に失敗しました。お電話でご予約ください。');
    } finally {
      setSubmitting(false);
    }
  };

  const isDateDisabled = (date) => {
    const dateStr = formatDate(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // 過去の日付
    if (date < today) return true;
    
    // 今日と明日（1日後）を無効化（2日後以降のみ選択可能）
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    
    if (dateOnly.getTime() <= tomorrow.getTime()) return true;
    
    // 休日
    if (holidays.includes(dateStr)) return true;
    
    // 2ヶ月先まで
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + storeConfig.advanceBookingMonths);
    if (date > maxDate) return true;
    
    return false;
  };

  // 予約完了画面をリセット
  const handleBackToForm = () => {
    setReservationComplete(null);
  };

  // 予約完了時に自動スクロール
  useEffect(() => {
    if (reservationComplete && completeSectionRef.current) {
      // 少し遅延させて、DOMが更新されてからスクロール
      setTimeout(() => {
        completeSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [reservationComplete]);

  // 確認モーダルのEscキー・背景スクロール制御
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !submitting) {
        setShowConfirmModal(false);
      }
    };

    if (showConfirmModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [showConfirmModal, submitting]);

  // 予約完了画面
  if (reservationComplete) {
    return (
      <section id="reservation" className="reservation-section" ref={completeSectionRef}>
        <div className="container">
          <h2 className="section-title">仮予約受付完了</h2>
          
          <div className="reservation-complete">
            <div className="complete-message">
              <p className="success-text">仮予約を受け付けました。</p>
              <p>まだご予約は確定しておりません。店舗からのご予約確定メールをお待ちください。翌営業日に返信がない場合、大変お手数ですが、ご確認のお電話をいただけますと幸いです。</p>
            </div>
            
            <div className="reservation-code-box">
              <p className="code-label">予約番号</p>
              <p className="reservation-code">{reservationComplete.code}</p>
              <p className="code-note">※予約確認・キャンセル時に必要です。必ずお控えください。</p>
            </div>
            
            <div className="reservation-summary">
              <h3>予約内容</h3>
              <p><strong>日時：</strong>{formatDateForDisplay(reservationComplete.date)} {formatTime(reservationComplete.time)}</p>
              <p><strong>人数：</strong>大人{reservationComplete.adultCount}名{reservationComplete.childCount > 0 && `、子供${reservationComplete.childCount}名`}</p>
              <p><strong>席：</strong>{reservationComplete.seatType === 'counter' ? 'カウンター席' : 'テーブル席'}</p>
              <p><strong>お名前：</strong>{reservationComplete.name}様</p>
            </div>
            
            <p className="email-notice">
              ※ご入力いただいたメールアドレス（{reservationComplete.email}）に予約番号をお送りしました。
            </p>
            
            <button 
              type="button" 
              className="btn-primary"
              onClick={handleBackToForm}
            >
              別の予約をする
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="reservation" className="reservation-section">
      <div className="container">
        <h2 className="section-title">オンライン席予約</h2>
        <p className="reservation-note">※席予約はお電話でも承っております。</p>
        <p className="reservation-note">※オンライン席予約では本日から2日後以降の予約が可能です。ご来店日の前日、もしくは当日のご予約はお電話で承ります。</p>
        
        <form className="reservation-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>予約日 *</label>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  value={formData.date ? formatDateForDisplay(formData.date) : ''}
                  placeholder="日付を選択"
                  readOnly
                  onClick={() => setShowCalendar(true)}
                />
              </div>
              {showCalendar && (
                <DatePickerCalendar
                  selectedDate={formData.date}
                  onSelect={(date) => {
                    // 日付が変わった場合は時間・席種をリセット（同日再選択なら維持）
                    setFormData(prev => (
                      prev.date === date ? prev : { ...prev, date, time: '', seatType: '' }
                    ));
                    setShowCalendar(false);
                  }}
                  onClose={() => setShowCalendar(false)}
                  isDateDisabled={isDateDisabled}
                />
              )}
            </div>
          </div>

          <div className="form-group">
            <label>時間 *</label>
            <select
              value={formData.time}
              onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
              disabled={!formData.date || loading}
            >
              <option value="">
                {loading ? '読み込み中...' : (formData.date ? '時間を選択' : '日付を先に選択してください')}
              </option>
              {availableTimes.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>大人 *</label>
              <select
                value={formData.adultCount}
                onChange={(e) => setFormData(prev => ({ ...prev, adultCount: parseInt(e.target.value) }))}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                  <option key={n} value={n}>{n}名</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>子供</label>
              <select
                value={formData.childCount}
                onChange={(e) => setFormData(prev => ({ ...prev, childCount: parseInt(e.target.value) }))}
              >
                {[0, 1, 2, 3, 4].map(n => (
                  <option key={n} value={n}>{n}名</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>席種 *</label>
            <select
              value={formData.seatType}
              onChange={(e) => setFormData(prev => ({ ...prev, seatType: e.target.value }))}
              disabled={!formData.time}
            >
              <option value="">
                {formData.time ? '席を選択' : '時間を先に選択してください'}
              </option>
              {availableSeats.map(seat => (
                <option key={seat.value} value={seat.value}>{seat.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>お名前（カタカナ） *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="ヤマダ　タロウ"
              required
            />
          </div>

          <div className="form-group">
            <label>電話番号 *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="09012345678"
              required
            />
          </div>

          <div className="form-group">
            <label>メールアドレス *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="example@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label>備考</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="アレルギーや特別なご要望があればご記入ください"
              rows={3}
            />
          </div>

          <button 
            type="submit" 
            className="btn-primary submit-btn"
            disabled={submitting}
          >
            予約する
          </button>
        </form>

        {showConfirmModal && (
          <div className="reservation-confirm-modal" onClick={handleCloseConfirmModal}>
            <div className="reservation-confirm-content" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="reservation-confirm-close"
                onClick={handleCloseConfirmModal}
                disabled={submitting}
                aria-label="閉じる"
              >
                ×
              </button>
              <h3 className="reservation-confirm-title">予約内容の確認</h3>
              <p className="reservation-confirm-note">以下の内容で仮予約します。よろしければ「予約する」を押してください。</p>
              <div className="reservation-confirm-summary">
                <p><strong>日時：</strong>{formatDateForDisplay(formData.date)} {formatTime(formData.time)}</p>
                <p><strong>人数：</strong>大人{formData.adultCount}名{formData.childCount > 0 && `、子供${formData.childCount}名`}</p>
                <p><strong>席：</strong>{getSeatLabel()}</p>
                <p><strong>お名前：</strong>{formData.name}様</p>
                <p><strong>電話番号：</strong>{formData.phone}</p>
                <p><strong>メール：</strong>{formData.email}</p>
                {formData.notes && <p><strong>備考：</strong>{formData.notes}</p>}
              </div>
              <div className="reservation-confirm-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseConfirmModal}
                  disabled={submitting}
                >
                  戻る
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleConfirmReservation}
                  disabled={submitting}
                >
                  {submitting ? '送信中...' : '予約する'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default ReservationSection;

